import React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Stack, Chip, Divider } from '@mui/material';
import QuizOutlinedIcon from '@mui/icons-material/QuizOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ReplayIcon from '@mui/icons-material/Replay';
import RateReviewIcon from '@mui/icons-material/RateReview';
import { useSubmitQuizAttemptMutation, useEvaluateQuizAttemptMutation } from '../../services/SkillsManagement';
import SeeMoreQuizEvaluation from './SeeMoreQuizEvaluation';

/**
 * AttepmtDialog
 * Handles submitting the attempt, evaluating it, and offering retry if attempts remain
 * Props:
 * - open: bool
 * - onClose: func
 * - attemptId: id of the current attempt
 * - maxAttempts: number
 * - currentAttempt: number (1-based)
 * - attemptsUsed: number (preferred for counts)
 * - onStartAgain: func -> called to start a new attempt from parent
 * - onFinish: func -> called when clicking Finish (should close parent too)
 * - quizId: number (for review answers)
 */
const AttepmtDialog = ({ open, onClose, attemptId, maxAttempts = 1, currentAttempt = 1, attemptsUsed, computedMaxScore, onStartAgain, onFinish, quizId }) => {
  const [submitAttempt, { isLoading: submitting }] = useSubmitQuizAttemptMutation();
  const [evaluateAttempt, { isLoading: evaluating }] = useEvaluateQuizAttemptMutation();
  const [reviewOpen, setReviewOpen] = React.useState(false);

  const [submittedId, setSubmittedId] = React.useState(null);
  const [scoreInfo, setScoreInfo] = React.useState(null);
  const used = Number.isFinite(Number(attemptsUsed)) ? Number(attemptsUsed) : (Number(currentAttempt) || 1);
  const remaining = Math.max(0, Number(maxAttempts) - used);

  React.useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!open || !attemptId) return;
      try {
        const sub = await submitAttempt({ attemptId }).unwrap();
        const finalId = sub?.data?.attemptId ?? sub?.attemptId ?? attemptId;
        if (cancelled) return;
        setSubmittedId(finalId);
        const ev = await evaluateAttempt({ attemptId: finalId }).unwrap();
        if (cancelled) return;
        // Try to normalize score fields
        const data = ev?.data || ev;
        const score = data?.score ?? data?.result?.score ?? null;
        const maxScore = data?.maxScore ?? data?.result?.maxScore ?? (Number(computedMaxScore) || null);
        const totalScore = data?.totalScore ?? (typeof score === 'number' ? score : null);
        const percentage = data?.percentage ?? (score != null && maxScore ? Math.round((score / maxScore) * 100) : data?.result?.percentage);
        setScoreInfo({ score, maxScore, totalScore, percentage });
      } catch (e) {
        // keep dialog open with generic message
        setSubmittedId(attemptId);
        setScoreInfo(null);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [open, attemptId, submitAttempt, evaluateAttempt]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        <Stack direction="row" spacing={1} alignItems="center">
          <QuizOutlinedIcon color="primary" />
          <Typography variant="h6">Attempt submitted</Typography>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1.5}>
          <Typography variant="body2" color="text.secondary">
            You have submitted your answers.
          </Typography>
          <Divider />
          <Stack direction="row" spacing={1} alignItems="center">
            <CheckCircleOutlineIcon color="success" fontSize="small" />
            <Typography variant="body2">Evaluation result</Typography>
          </Stack>
          {(submitting || evaluating) && (
            <Typography variant="body2" color="text.secondary">Calculating your score...</Typography>
          )}
          {!(submitting || evaluating) && (
            <Stack direction="row" spacing={1} alignItems="center">
              {scoreInfo?.score != null && scoreInfo?.maxScore != null && (
                <Chip color="primary" label={`Obtained ${scoreInfo.score} / ${scoreInfo.maxScore}`} />
              )}
              {scoreInfo?.totalScore != null && scoreInfo?.maxScore != null && scoreInfo?.score == null && (
                <Chip color="primary" label={`Obtained ${scoreInfo.totalScore} / ${scoreInfo.maxScore}`} />
              )}
            </Stack>
          )}
          <Divider />
          <Typography variant="body2" color="text.secondary">
            Attempts used: {Math.min(used, Number(maxAttempts) || 1)} / {Number(maxAttempts) || 1}
          </Typography>
          {remaining > 0 && (
            <Typography variant="body2">You have {remaining} attempt(s) remaining. Do you want to try again?</Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button startIcon={<RateReviewIcon />} onClick={() => setReviewOpen(true)} disabled={submitting || evaluating}>
          Review answers
        </Button>
        {remaining > 0 && (
          <Button startIcon={<ReplayIcon />} onClick={onStartAgain} disabled={submitting || evaluating}>
            Start again
          </Button>
        )}
        <Button variant="contained" onClick={onFinish || onClose} disabled={submitting || evaluating}>Finish</Button>
      </DialogActions>
      <SeeMoreQuizEvaluation
        open={reviewOpen}
        onClose={() => setReviewOpen(false)}
        attemptId={attemptId}
        quizId={quizId}
      />
    </Dialog>
  );
};

export default AttepmtDialog;

